import axios from 'axios';
import toast from 'react-hot-toast';
import { store } from '../store';
import { logout } from '../store/slices/authSlice';

const BASE_URL = process.env.REACT_APP_API_URL || '/api/v1';
const SALESFORCE_SYNC_TIMEOUT_MS = 10 * 60 * 1000;

// ── Global loading tracker ────────────────────────────────────────────────
// Counts in-flight requests; fires 'api-loading' events so GlobalLoader can react.
let _pending = 0;
function _notifyLoading(active) {
  window.dispatchEvent(new CustomEvent('api-loading', { detail: { active } }));
}

// ── Authenticated axios instance (adds Bearer token to every request) ─────
const api = axios.create({
  baseURL: BASE_URL,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use(
  (config) => {
    if (++_pending === 1) _notifyLoading(true);
    const token = localStorage.getItem('accessToken');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => {
    if (--_pending <= 0) { _pending = 0; _notifyLoading(false); }
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => {
    if (--_pending <= 0) { _pending = 0; _notifyLoading(false); }
    return response.data;
  },
  (error) => {
    if (--_pending <= 0) { _pending = 0; _notifyLoading(false); }
    const status = error.response?.status;
    const message = error.response?.data?.error || 'An unexpected error occurred';
    if (status === 401) {
      store.dispatch(logout());
      toast.error('Session expired. Please log in again.');
    } else if (status === 403) {
      toast.error('You do not have permission for this action.');
    } else if (status >= 500) {
      toast.error('Server error. Please try again later.');
    }
    return Promise.reject(error);
  }
);

export default api;

// ── Public axios instance (no auth header, no redirect on 401) ───────────
// Used for unauthenticated endpoints such as the landing-page inquiry form.
const publicApi = axios.create({
  baseURL: BASE_URL,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});
publicApi.interceptors.response.use((response) => response.data);

// ── API groups ────────────────────────────────────────────────────────────

export const authApi = {
  login: (data) => api.post('/auth/login', data),
};

// Public — no JWT required
export const inquiryPublicApi = {
  submitInquiry: (data) => publicApi.post('/public/inquiries', data),
};

export const adminApi = {
  // Platform user management  (PLATFORM_SUPER_ADMIN / PLATFORM_ADMIN)
  createPlatformUser:   (data)    => api.post('/admin/users', data),
  getPlatformUsers:     (params)  => api.get('/admin/users', { params }),
  getAllUsers:          (params)  => api.get('/admin/all-users', { params }),
  updatePlatformUser:   (id, data) => api.put(`/admin/users/${id}`, data),
  toggleUserActive:     (id)      => api.patch(`/admin/users/${id}/toggle-active`),
  getUserAuditLog:      (id)      => api.get(`/admin/users/${id}/audit-log`),

  // Finished-goods operations
  getFinishedGoods:     (params)  => api.get('/admin/finished-goods', { params }),
  getFinishedGood:      (id)      => api.get(`/admin/finished-goods/${id}`),
  assignVendors:        (data)    => api.post('/admin/assignments', data),
  getAssignment:        (id)      => api.get(`/admin/assignments/${id}`),
  getAssignmentHistory: (id)      => api.get(`/admin/assignments/${id}/history`),
  withdrawAssignment:   (id)      => api.delete(`/admin/assignments/${id}`),
  cancelAssignment:     (id, reason) => api.post(`/admin/assignments/${id}/cancel`, null, { params: reason ? { reason } : {} }),
  companyReview:        (data)    => api.post('/admin/review/company', data),
  // Dashboard  (optional params: clientId, dateFrom yyyy-MM-dd, dateTo yyyy-MM-dd)
  getDashboardStats:    (params)  => api.get('/admin/dashboard/stats', { params }),
  getActivityTrend:     (params)  => api.get('/admin/dashboard/trend', { params }),

  // Leads / Inquiries  (PLATFORM_SUPER_ADMIN / PLATFORM_ADMIN only)
  getInquiries:         (params)     => api.get('/admin/inquiries', { params }),
  getInquiryCountNew:   ()           => api.get('/admin/inquiries/count-new'),
  updateInquiryStatus:  (id, status) => api.patch(`/admin/inquiries/${id}/status`, null, { params: { status } }),

  // Quick Assign — auto-assigns the vendor already embedded in the Finished Good
  quickAssign:          (finishedGoodId) => api.post(`/admin/finished-goods/${finishedGoodId}/quick-assign`),

  // Salesforce — PLATFORM_SUPER_ADMIN only
  // Lists all active CLIENT companies from our DB for the account-selection modal.
  getSalesforceAccounts: ()         => api.get('/admin/salesforce/accounts'),
  // A manual full-catalog sync can take several minutes, so it must not inherit
  // the 30-second timeout used by regular interactive API calls.
  salesforceSync:        (clientId) => api.post('/admin/salesforce/sync', null, {
    params: { clientId },
    timeout: SALESFORCE_SYNC_TIMEOUT_MS,
  }),
  // Manual-only full catalog refresh. This has a separate API so it can never
  // be invoked by the incremental scheduler or normal Salesforce Sync action.
  salesforceFullSync:    (clientId) => api.post('/admin/salesforce/full-sync', null, {
    params: { clientId },
    timeout: SALESFORCE_SYNC_TIMEOUT_MS,
  }),

  // BOM in-place edit during OmniDrive review (status must be VENDOR_SUBMITTED)
  updateBom:            (assignmentId, data) => api.patch(`/admin/assignments/${assignmentId}/bom`, data),
  // Delete a single BOM component row
  deleteBomComponent:   (assignmentId, componentId) => api.delete(`/admin/assignments/${assignmentId}/bom/${componentId}`),

  // Assignment Notes
  addAssignmentNote:    (assignmentId, data) => api.post(`/admin/assignments/${assignmentId}/notes`, data),
  getAssignmentNotes:   (assignmentId)       => api.get(`/admin/assignments/${assignmentId}/notes`),
  // Super-admin-only note moderation
  updateAssignmentNote: (assignmentId, noteId, data) => api.put(`/admin/assignments/${assignmentId}/notes/${noteId}`, data),
  deleteAssignmentNote: (assignmentId, noteId)        => api.delete(`/admin/assignments/${assignmentId}/notes/${noteId}`),

  // Notifications
  getNotifications:         ()   => api.get('/admin/notifications'),
  markAllNotificationsRead: ()   => api.patch('/admin/notifications/read-all'),
  markNotificationRead:     (id) => api.patch(`/admin/notifications/${id}/read`),
};

// ── Company management  (PLATFORM_SUPER_ADMIN / PLATFORM_ADMIN) ─────────────
export const companyApi = {
  // Company CRUD
  createCompany:          (data)           => api.post('/companies', data),
  getAllCompanies:         (params)         => api.get('/companies', { params }),
  getCompany:             (id)             => api.get(`/companies/${id}`),
  updateCompany:          (id, data)       => api.put(`/companies/${id}`, data),
  toggleCompanyActive:    (id)             => api.patch(`/companies/${id}/toggle-active`),

  // Seed the first ADMIN for a newly created company (platform admins only)
  seedCompanyAdmin:       (companyId, data) => api.post(`/companies/${companyId}/admin-user`, data),

  // Company-scoped user management
  // Used by: PLATFORM_SUPER_ADMIN, PLATFORM_ADMIN, CLIENT_ADMIN, VENDOR_ADMIN
  getCompanyUsers:        (companyId, params)         => api.get(`/companies/${companyId}/users`, { params }),
  createCompanyUser:      (companyId, data)            => api.post(`/companies/${companyId}/users`, data),
  updateCompanyUser:      (companyId, userId, data)    => api.put(`/companies/${companyId}/users/${userId}`, data),
  toggleCompanyUserActive:(companyId, userId)          => api.patch(`/companies/${companyId}/users/${userId}/toggle-active`),

  // ── Vendor bulk import (PLATFORM_SUPER_ADMIN only) ──────────────────
  /**
   * Download the pre-formatted vendor import template (.xlsx).
   * Triggers a browser file download directly.
   */
  downloadVendorTemplate: () => {
    const token = localStorage.getItem('accessToken');
    const link  = document.createElement('a');
    link.href   = `${BASE_URL}/companies/vendors/import/template`;
    // axios interceptor won't run here — attach token via fetch instead
    fetch(`${BASE_URL}/companies/vendors/import/template`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.blob())
      .then((blob) => {
        const url  = URL.createObjectURL(blob);
        link.href  = url;
        link.download = 'vendor_import_template.xlsx';
        link.click();
        URL.revokeObjectURL(url);
      });
  },

  /**
   * Upload a filled-in vendor import Excel file.
   * Returns ApiResponse<VendorImportResult>.
   */
  /**
   * Step 1 — Parse and validate the Excel file server-side; returns a preview
   * with per-row status (VALID / INVALID / DUPLICATE). Nothing is saved yet.
   */
  previewImport: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/companies/vendors/import/preview', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  /**
   * Step 2 — Send the previewed rows back to save VALID ones to the database.
   */
  confirmImport: (rows) => api.post('/companies/vendors/import/confirm', { rows }),
};

export const vendorApi = {
  getAssignments:       (params)          => api.get('/vendor/assignments', { params }),
  getAssignment:        (id)              => api.get(`/vendor/assignments/${id}`),
  getAssignmentHistory: (id)              => api.get(`/vendor/assignments/${id}/history`),
  addAssignmentNote:    (id, data)        => api.post(`/vendor/assignments/${id}/notes`, data),
  getAssignmentNotes:   (id)             => api.get(`/vendor/assignments/${id}/notes`),

  // Notifications
  getNotifications:         ()   => api.get('/vendor/notifications'),
  markAllNotificationsRead: ()   => api.patch('/vendor/notifications/read-all'),
  markNotificationRead:     (id) => api.patch(`/vendor/notifications/${id}/read`),
  submitPartArticle:    (data)            => api.post('/vendor/part-articles', data),
  uploadFile:           (partArticleId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/vendor/part-articles/${partArticleId}/files`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteFile:           (assignmentId, partArticleId, fileId) =>
    api.delete(`/vendor/assignments/${assignmentId}/part-articles/${partArticleId}/files/${fileId}`),
  saveAsDraft:          (id, data)        => api.post(`/vendor/assignments/${id}/save-draft`, data),
  updatePartArticleCategory: (assignmentId, partArticleId, category) =>
    api.patch(`/vendor/assignments/${assignmentId}/part-articles/${partArticleId}/category`, { category }),
  submitAssignment:     (id)              => api.post(`/vendor/assignments/${id}/submit`),
  deleteBomComponent:   (assignmentId, componentId) => api.delete(`/vendor/assignments/${assignmentId}/bom/${componentId}`),
  getDashboardStats:    ()                => api.get('/vendor/dashboard/stats'),

  /**
   * Fetches an uploaded file's raw content from S3 (via the backend) as a
   * Base64-encoded string.
   *
   * Returns: { fileId, contentType, base64Data, originalName, s3Key }
   *
   * Render in JSX:
   *   <img src={`data:${res.data.contentType};base64,${res.data.base64Data}`} />
   */
  getFileContent:       (fileId)          => api.get(`/vendor/files/${fileId}/content`),

  /**
   * Download the Part Article BOM upload template (.xlsx).
   * Triggers a browser file-save dialog directly.
   */
  downloadPartArticleTemplate: () => {
    const token = localStorage.getItem('accessToken');
    return fetch(`${BASE_URL}/vendor/part-articles/template`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => {
        if (!res.ok) throw new Error(`Template download failed (${res.status})`);
        return res.blob();
      })
      .then((blob) => {
        const url  = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href  = url;
        link.download = 'part_article_upload_template.xlsx';
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.setTimeout(() => URL.revokeObjectURL(url), 0);
      });
  },
};

export const clientApi = {
  getDashboardStats:    ()       => api.get('/client/dashboard/stats'),
  getActivityTrend:     (params) => api.get('/client/dashboard/trend', { params }),
  getFinishedGoods:     (params) => api.get('/client/finished-goods', { params }),
  getFinishedGood:      (id)     => api.get(`/client/finished-goods/${id}`),
  getAssignment:        (id)     => api.get(`/client/assignments/${id}`),
  getAssignmentHistory: (id)     => api.get(`/client/assignments/${id}/history`),
  addAssignmentNote:    (id, data) => api.post(`/client/assignments/${id}/notes`, data),
  getAssignmentNotes:   (id)      => api.get(`/client/assignments/${id}/notes`),
  clientReview:         (data)   => api.post('/client/review/client', data),
  finalApproval:        (id, notes) => api.post(`/client/assignments/${id}/final-approval`, { notes }),
  finalReject:          (id, notes) => api.post(`/client/assignments/${id}/final-reject`, { notes }),
  // BOM in-place edit during client review (status must be COMPANY_APPROVED)
  updateBom:            (assignmentId, data) => api.patch(`/client/assignments/${assignmentId}/bom`, data),
  // Delete a single BOM component row
  deleteBomComponent:   (assignmentId, componentId) => api.delete(`/client/assignments/${assignmentId}/bom/${componentId}`),

  // Notifications
  getNotifications:         ()   => api.get('/client/notifications'),
  markAllNotificationsRead: ()   => api.patch('/client/notifications/read-all'),
  markNotificationRead:     (id) => api.patch(`/client/notifications/${id}/read`),
};

/**
 * Role-agnostic file API — any authenticated user (vendor, company, client,
 * platform) can call these endpoints.
 *
 * Usage:
 *   const res = await fileApi.getFileContent(fileId);
 *   // res.data = { fileId, contentType, base64Data, originalName, s3Key }
 *   <img src={`data:${res.data.contentType};base64,${res.data.base64Data}`} />
 */
export const fileApi = {
  getFileContent: (fileId) => api.get(`/files/${fileId}/content`),
};
